import type { Story, StorySectionId } from './story-types'
import type { ChatContext } from './chat-types'
import type { ConversationPhase, ConversationState } from './conversation-types'
import { INITIAL_CONVERSATION_STATE } from './conversation-types'

/**
 * Chat with no surface under it — the context a session has before any
 * project is open, and the one it must go back to when a project is left.
 *
 * ONE source for two consumers: the factory below starts here, and
 * `project-session-exit.ts` returns here. A populated `chatContext` names an
 * authority domain/view, a surface id and a selected property key — all of
 * them coordinates INSIDE one project — and
 * `getStudioComposerPlaceholder` (features/chat/services/
 * studio-task-orchestrator.ts:385) branches on `authorityDomain &&
 * authorityView` before it looks at anything else. Left pinned, it makes the
 * composer on the dashboard talk about a surface of a project that is closed.
 */
export const INITIAL_CHAT_CONTEXT: ChatContext = { mode: 'chat' }

export interface StorySlice {
  story: Story | null
  scrollTarget: StorySectionId | null
  chatContext: ChatContext
  conversation: ConversationState
  setStory: (story: Story | null) => void
  updateNarrative: (text: string) => void
  updateGoals: (goals: string[]) => void
  setScrollTarget: (target: StorySectionId | null) => void
  clearScrollTarget: () => void
  setChatContext: (context: ChatContext) => void
  setConversationPhase: (phase: ConversationPhase) => void
  setConversation: (state: Partial<ConversationState>) => void
  resetConversation: () => void
}

type Set = (fn: (state: StorySlice) => void) => void

export function createStorySlice(
  set: Set,
  _get: unknown,
  _api: unknown,
): StorySlice {
  return {
    story: null,
    scrollTarget: null,
    chatContext: { ...INITIAL_CHAT_CONTEXT },
    conversation: INITIAL_CONVERSATION_STATE,
    setStory: (story) => set((s) => { s.story = story }),
    updateNarrative: (text) => set((s) => {
      if (s.story) s.story.narrative = text
    }),
    updateGoals: (goals) => set((s) => {
      if (s.story) s.story.goals = goals
    }),
    setScrollTarget: (target) => set((s) => { s.scrollTarget = target }),
    clearScrollTarget: () => set((s) => { s.scrollTarget = null }),
    setChatContext: (context) => set((s) => { s.chatContext = context }),
    setConversationPhase: (phase) => set((s) => { s.conversation.phase = phase }),
    setConversation: (partial) => set((s) => { Object.assign(s.conversation, partial) }),
    resetConversation: () => set((s) => { s.conversation = INITIAL_CONVERSATION_STATE }),
  }
}
